import React, { useEffect, useRef, useState, useCallback } from 'react'
import synth from './audioSynth'

const STORAGE_HIGH_SCORE = 'captain_popx_high_score'

const CaptainPopX = ({ isAppReady = false, onExitToDashboard, onSwitchGame }) => {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)

  // Game UI States
  const [gameState, setGameState] = useState('PLAYING') // 'PLAYING' | 'GAMEOVER'
  const [score, setScore] = useState(0)
  const [highScore, setHighScore] = useState(() => {
    try {
      return parseInt(localStorage.getItem(STORAGE_HIGH_SCORE), 10) || 0
    } catch (e) {
      return 0
    }
  })
  const [lives, setLives] = useState(3)
  const [isMuted, setIsMuted] = useState(synth.isMuted())
  const [isDismissedReadyNotice, setIsDismissedReadyNotice] = useState(false)
  const [isNewHighScore, setIsNewHighScore] = useState(false)

  // Direct DOM refs to avoid 60fps React re-renders
  const scoreValRef = useRef(null)
  const highScoreValRef = useRef(null)
  const heartsRef = useRef([])
  const onExitRef = useRef(onExitToDashboard)
  onExitRef.current = onExitToDashboard

  const updateHeartsDOM = (liveCount) => {
    heartsRef.current.forEach((el, i) => {
      if (!el) return
      el.className = `captain-popx__heart ${i < liveCount ? 'active' : 'empty'}`
    })
  }

  // Internal mutable state for the 60fps canvas loop
  const loopState = useRef({
    score: 0,
    highScore: 0,
    lives: 3,
    running: true,
    gameOver: false,
    keys: { left: false, right: false, up: false, down: false, shoot: false },
    mouse: { x: null, y: null, down: false },
    lastShotTime: 0,
    shotCooldown: 180, // ms between shots
    player: {
      x: 270,
      y: 560,
      width: 44,
      height: 48,
      speed: 6.5,
      invulnerableTimer: 0
    },
    stars: [],
    bullets: [],
    enemies: [],
    enemyBullets: [],
    particles: [],
    powerups: [],
    spawnTimer: 0,
    enemySpawnRate: 75,
    frameCount: 0
  })

  // Sync state refs
  useEffect(() => {
    loopState.current.highScore = highScore
  }, [highScore])

  // Sound toggle handler
  const handleToggleMute = () => {
    const nextMuted = synth.toggleMute()
    setIsMuted(nextMuted)
  }

  // Restart Game
  const handleRestart = useCallback(() => {
    const s = loopState.current
    s.score = 0
    s.lives = 3
    s.running = true
    s.gameOver = false
    s.bullets = []
    s.enemies = []
    s.enemyBullets = []
    s.particles = []
    s.powerups = []
    s.player.x = 270
    s.player.y = 560
    s.player.invulnerableTimer = 60
    s.spawnTimer = 0
    s.enemySpawnRate = 75

    if (scoreValRef.current) scoreValRef.current.textContent = '0'
    updateHeartsDOM(3)
    setScore(0)
    setLives(3)
    setIsNewHighScore(false)
    setGameState('PLAYING')
  }, [])

  // Keyboard handlers
  useEffect(() => {
    const handleKeyDown = (e) => {
      const keys = loopState.current.keys
      if (e.code === 'ArrowLeft' || e.key === 'a' || e.key === 'A') keys.left = true
      if (e.code === 'ArrowRight' || e.key === 'd' || e.key === 'D') keys.right = true
      if (e.code === 'ArrowUp' || e.key === 'w' || e.key === 'W') keys.up = true
      if (e.code === 'ArrowDown' || e.key === 's' || e.key === 'S') keys.down = true
      if (e.code === 'Space') {
        keys.shoot = true
        e.preventDefault()
      }
      if (e.key === 'Escape') {
        if (onExitRef.current) onExitRef.current()
      }
    }

    const handleKeyUp = (e) => {
      const keys = loopState.current.keys
      if (e.code === 'ArrowLeft' || e.key === 'a' || e.key === 'A') keys.left = false
      if (e.code === 'ArrowRight' || e.key === 'd' || e.key === 'D') keys.right = false
      if (e.code === 'ArrowUp' || e.key === 'w' || e.key === 'W') keys.up = false
      if (e.code === 'ArrowDown' || e.key === 's' || e.key === 'S') keys.down = false
      if (e.code === 'Space') keys.shoot = false
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  // Mouse / Touch movement handlers
  const handlePointerMove = (e) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    loopState.current.mouse.x = (e.clientX - rect.left) * scaleX
    loopState.current.mouse.y = (e.clientY - rect.top) * scaleY
  }

  const handlePointerDown = () => {
    loopState.current.mouse.down = true
  }

  const handlePointerUp = () => {
    loopState.current.mouse.down = false
    loopState.current.mouse.x = null
    loopState.current.mouse.y = null
  }

  // Main Canvas Render Loop
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    const width = 540
    const height = 660
    canvas.width = width
    canvas.height = height

    const state = loopState.current

    // Initialize parallax stars
    state.stars = []
    for (let i = 0; i < 75; i++) {
      state.stars.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 2 + 0.6,
        speed: Math.random() * 1.5 + 0.5,
        alpha: Math.random() * 0.7 + 0.3
      })
    }

    let animationFrameId

    const spawnParticles = (x, y, count = 12, colors = ['#38b3be', '#5FC556', '#FF6647', '#DDFAFF']) => {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2
        const speed = Math.random() * 4 + 1
        state.particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1,
          decay: Math.random() * 0.03 + 0.02,
          size: Math.random() * 3.5 + 1.5,
          color: colors[Math.floor(Math.random() * colors.length)]
        })
      }
    }

    const drawPlayer = (x, y, invulnerableTimer, frameCount) => {
      ctx.save()
      ctx.translate(x, y)

      // Flicker if invulnerable
      if (invulnerableTimer > 0 && Math.floor(frameCount / 4) % 2 === 0) {
        ctx.restore()
        return
      }

      // Thruster flame animation (PopEx Orange & Yellow)
      const flameLength = 12 + Math.sin(frameCount * 0.5) * 5
      ctx.beginPath()
      ctx.moveTo(-8, 22)
      ctx.lineTo(0, 22 + flameLength)
      ctx.lineTo(8, 22)
      ctx.fillStyle = '#FF6647'
      ctx.fill()

      ctx.beginPath()
      ctx.moveTo(-4, 22)
      ctx.lineTo(0, 22 + flameLength * 0.7)
      ctx.lineTo(4, 22)
      ctx.fillStyle = '#FFD54F'
      ctx.fill()

      // Outer Hull (PopEx Teal & Deep Blue)
      ctx.beginPath()
      ctx.moveTo(0, -24) // Nose tip
      ctx.lineTo(20, 16) // Right wing tip
      ctx.lineTo(14, 22) // Right wing notch
      ctx.lineTo(6, 18) // Right engine mount
      ctx.lineTo(-6, 18) // Left engine mount
      ctx.lineTo(-14, 22) // Left wing notch
      ctx.lineTo(-20, 16) // Left wing tip
      ctx.closePath()

      // Gradient Fill matching app theme
      const hullGrad = ctx.createLinearGradient(0, -24, 0, 22)
      hullGrad.addColorStop(0, '#38b3be')
      hullGrad.addColorStop(0.6, '#1d2f35')
      hullGrad.addColorStop(1, '#052A30')
      ctx.fillStyle = hullGrad
      ctx.fill()

      ctx.strokeStyle = '#38b3be'
      ctx.lineWidth = 1.8
      ctx.stroke()

      // Wing Canards / Accent Trim (Neon Green & Cyan)
      ctx.beginPath()
      ctx.moveTo(0, -12)
      ctx.lineTo(12, 10)
      ctx.lineTo(0, 8)
      ctx.lineTo(-12, 10)
      ctx.closePath()
      ctx.fillStyle = '#2B464F'
      ctx.fill()
      ctx.strokeStyle = '#5FC556'
      ctx.lineWidth = 1
      ctx.stroke()

      // Cockpit Glass
      ctx.beginPath()
      ctx.ellipse(0, -4, 4, 9, 0, 0, Math.PI * 2)
      ctx.fillStyle = '#DDFAFF'
      ctx.fill()

      // PopEx Branded Insignia on the craft
      ctx.font = 'bold 8px sans-serif'
      ctx.fillStyle = '#38b3be'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('PopEx', 0, 12)

      // Twin Laser Cannons
      ctx.fillStyle = '#5FC556'
      ctx.fillRect(-18, 4, 3, 10)
      ctx.fillRect(15, 4, 3, 10)

      ctx.restore()
    }

    const drawEnemy = (e) => {
      ctx.save()
      ctx.translate(e.x, e.y)
      ctx.rotate(e.rotation)

      if (e.type === 'asteroid') {
        // Jagged Space Asteroid (Deep space rock with cyan energy veins)
        ctx.beginPath()
        const pts = e.points
        ctx.moveTo(pts[0].x, pts[0].y)
        for (let i = 1; i < pts.length; i++) {
          ctx.lineTo(pts[i].x, pts[i].y)
        }
        ctx.closePath()
        ctx.fillStyle = '#1e2830'
        ctx.fill()
        ctx.strokeStyle = e.hp > 1 ? '#38b3be' : '#FF6647'
        ctx.lineWidth = 1.8
        ctx.stroke()

        // Inner glowing core
        ctx.beginPath()
        ctx.arc(0, 0, e.radius * 0.35, 0, Math.PI * 2)
        ctx.fillStyle = e.hp > 1 ? 'rgba(56, 179, 190, 0.25)' : 'rgba(255, 102, 71, 0.35)'
        ctx.fill()
      } else {
        // Enemy Scout Drone
        ctx.beginPath()
        ctx.moveTo(0, 16)
        ctx.lineTo(14, -12)
        ctx.lineTo(0, -6)
        ctx.lineTo(-14, -12)
        ctx.closePath()
        ctx.fillStyle = '#261b24'
        ctx.fill()
        ctx.strokeStyle = '#FF6647'
        ctx.lineWidth = 1.6
        ctx.stroke()

        // Evil Red/Orange Eye
        ctx.beginPath()
        ctx.arc(0, 0, 3.5, 0, Math.PI * 2)
        ctx.fillStyle = '#FF6647'
        ctx.shadowColor = '#FF6647'
        ctx.shadowBlur = 6
        ctx.fill()
        ctx.shadowBlur = 0
      }

      ctx.restore()
    }

    const drawPowerup = (p) => {
      ctx.save()
      ctx.translate(p.x, p.y)
      ctx.beginPath()
      ctx.arc(0, 0, 10, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(95, 197, 86, 0.25)'
      ctx.fill()
      ctx.strokeStyle = '#5FC556'
      ctx.lineWidth = 1.5
      ctx.stroke()

      ctx.font = 'bold 10px sans-serif'
      ctx.fillStyle = '#5FC556'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('+', 0, 0)
      ctx.restore()
    }

    const gameLoop = () => {
      state.frameCount++

      // 1. CLEAR & BACKGROUND
      ctx.fillStyle = '#0f181c'
      ctx.fillRect(0, 0, width, height)

      // Parallax Stars
      state.stars.forEach(star => {
        star.y += star.speed
        if (star.y > height) {
          star.y = 0
          star.x = Math.random() * width
        }
        ctx.fillStyle = `rgba(221, 250, 255, ${star.alpha})`
        ctx.fillRect(star.x, star.y, star.size, star.size)
      })

      // Active Game Logic
      if (state.running) {
        // 2. PLAYER MOVEMENT
        const p = state.player
        const keys = state.keys
        const mouse = state.mouse

        if (mouse.x !== null && mouse.y !== null) {
          // Smooth follow pointer
          const dx = mouse.x - p.x
          const dy = mouse.y - p.y
          p.x += dx * 0.15
          p.y += dy * 0.15
        } else {
          if (keys.left) p.x -= p.speed
          if (keys.right) p.x += p.speed
          if (keys.up) p.y -= p.speed
          if (keys.down) p.y += p.speed
        }

        // Keep inside bounds
        p.x = Math.max(22, Math.min(width - 22, p.x))
        p.y = Math.max(40, Math.min(height - 35, p.y))

        if (p.invulnerableTimer > 0) p.invulnerableTimer--

        // 3. SHOOTING
        const now = Date.now()
        const wantsShoot = keys.shoot || mouse.down
        if (wantsShoot && now - state.lastShotTime > state.shotCooldown) {
          state.lastShotTime = now
          // Twin lasers from left & right wings
          state.bullets.push({ x: p.x - 16, y: p.y - 10, vy: -12, color: '#38b3be' })
          state.bullets.push({ x: p.x + 16, y: p.y - 10, vy: -12, color: '#38b3be' })
          synth.playLaser()
        }

        // 4. SPAWN ENEMIES & OBSTACLES
        state.spawnTimer++
        if (state.spawnTimer >= state.enemySpawnRate) {
          state.spawnTimer = 0
          // Speed up slightly as score rises
          state.enemySpawnRate = Math.max(35, 75 - Math.floor(state.score / 200) * 3)

          const isDrone = Math.random() > 0.65
          if (isDrone) {
            state.enemies.push({
              type: 'drone',
              x: Math.random() * (width - 60) + 30,
              y: -25,
              vx: (Math.random() - 0.5) * 2.5,
              vy: Math.random() * 1.8 + 1.6,
              hp: 2,
              radius: 14,
              rotation: 0,
              shootTimer: Math.floor(Math.random() * 40)
            })
          } else {
            // Jagged Asteroid
            const radius = Math.random() * 14 + 16
            const numPoints = Math.floor(Math.random() * 4) + 7
            const points = []
            for (let i = 0; i < numPoints; i++) {
              const angle = (i / numPoints) * Math.PI * 2
              const r = radius * (0.75 + Math.random() * 0.5)
              points.push({ x: Math.cos(angle) * r, y: Math.sin(angle) * r })
            }
            state.enemies.push({
              type: 'asteroid',
              x: Math.random() * (width - 60) + 30,
              y: -radius - 10,
              vx: (Math.random() - 0.5) * 1.2,
              vy: Math.random() * 1.8 + 1.2,
              hp: radius > 22 ? 2 : 1,
              radius,
              points,
              rotation: Math.random() * Math.PI * 2,
              rotSpeed: (Math.random() - 0.5) * 0.04
            })
          }
        }

        // 5. UPDATE BULLETS
        for (let i = state.bullets.length - 1; i >= 0; i--) {
          const b = state.bullets[i]
          b.y += b.vy
          if (b.y < -10) {
            state.bullets.splice(i, 1)
          }
        }

        // 6. UPDATE ENEMY BULLETS
        for (let i = state.enemyBullets.length - 1; i >= 0; i--) {
          const eb = state.enemyBullets[i]
          eb.y += eb.vy
          eb.x += eb.vx || 0

          // Collision with player
          const dist = Math.hypot(eb.x - p.x, eb.y - p.y)
          if (dist < 18 && p.invulnerableTimer <= 0) {
            state.enemyBullets.splice(i, 1)
            state.lives--
            setLives(state.lives)
            p.invulnerableTimer = 60
            synth.playHit()
            spawnParticles(p.x, p.y, 16, ['#FF6647', '#FFD54F', '#ffffff'])

            if (state.lives <= 0) {
              state.running = false
              state.gameOver = true
              synth.playGameOver()
              setGameState('GAMEOVER')
            }
            continue
          }

          if (eb.y > height + 20) {
            state.enemyBullets.splice(i, 1)
          }
        }

        // 7. UPDATE ENEMIES & COLLISIONS
        for (let i = state.enemies.length - 1; i >= 0; i--) {
          const e = state.enemies[i]
          e.x += e.vx
          e.y += e.vy
          e.rotation += e.rotSpeed || 0

          // Bounce off left/right edges
          if (e.x < e.radius || e.x > width - e.radius) e.vx *= -1

          // Enemy scout shoots
          if (e.type === 'drone') {
            e.shootTimer++
            if (e.shootTimer > 80) {
              e.shootTimer = 0
              state.enemyBullets.push({
                x: e.x,
                y: e.y + 12,
                vy: 5,
                color: '#FF6647'
              })
            }
          }

          // Bullet hits Enemy
          let enemyDestroyed = false
          for (let j = state.bullets.length - 1; j >= 0; j--) {
            const b = state.bullets[j]
            const dist = Math.hypot(b.x - e.x, b.y - e.y)
            if (dist < e.radius + 6) {
              state.bullets.splice(j, 1)
              e.hp--
              spawnParticles(b.x, b.y, 5, ['#38b3be', '#DDFAFF'])

              if (e.hp <= 0) {
                enemyDestroyed = true
                synth.playExplosion()
                spawnParticles(e.x, e.y, 18, ['#38b3be', '#5FC556', '#FF6647'])

                // Random powerup chance (1 in 8)
                if (Math.random() < 0.12 && state.lives < 3) {
                  state.powerups.push({ x: e.x, y: e.y, vy: 1.8 })
                }

                const pointsAwarded = e.type === 'drone' ? 150 : 80
                state.score += pointsAwarded
                if (scoreValRef.current) {
                  scoreValRef.current.textContent = state.score
                }

                // High score update
                if (state.score > state.highScore) {
                  state.highScore = state.score
                  if (highScoreValRef.current) {
                    highScoreValRef.current.textContent = state.score
                  }
                }
                break
              } else {
                synth.playHit()
              }
            }
          }

          if (enemyDestroyed) {
            state.enemies.splice(i, 1)
            continue
          }

          // Enemy collides with Player
          const distToPlayer = Math.hypot(e.x - p.x, e.y - p.y)
          if (distToPlayer < e.radius + 14 && p.invulnerableTimer <= 0) {
            state.enemies.splice(i, 1)
            state.lives--
            updateHeartsDOM(state.lives)
            p.invulnerableTimer = 60
            synth.playHit()
            spawnParticles(p.x, p.y, 20, ['#FF6647', '#FFD54F', '#ffffff'])

            if (state.lives <= 0) {
              state.running = false
              state.gameOver = true
              synth.playGameOver()
              setScore(state.score)
              setHighScore(state.highScore)
              if (state.score > highScore) {
                setIsNewHighScore(true)
                try {
                  localStorage.setItem(STORAGE_HIGH_SCORE, state.score.toString())
                } catch (err) {}
              }
              setLives(0)
              setGameState('GAMEOVER')
            }
            continue
          }

          // Off screen bottom
          if (e.y > height + 50) {
            state.enemies.splice(i, 1)
          }
        }

        // 8. UPDATE POWERUPS
        for (let i = state.powerups.length - 1; i >= 0; i--) {
          const pow = state.powerups[i]
          pow.y += pow.vy
          const dist = Math.hypot(pow.x - p.x, pow.y - p.y)
          if (dist < 26) {
            state.powerups.splice(i, 1)
            if (state.lives < 3) {
              state.lives++
              updateHeartsDOM(state.lives)
            }
            synth.playPowerup()
            spawnParticles(pow.x, pow.y, 14, ['#5FC556', '#DDFAFF'])
            continue
          }
          if (pow.y > height + 20) {
            state.powerups.splice(i, 1)
          }
        }
      }

      // 9. UPDATE PARTICLES
      for (let i = state.particles.length - 1; i >= 0; i--) {
        const pt = state.particles[i]
        pt.x += pt.vx
        pt.y += pt.vy
        pt.life -= pt.decay
        if (pt.life <= 0) {
          state.particles.splice(i, 1)
          continue
        }
        ctx.fillStyle = pt.color
        ctx.globalAlpha = Math.max(0, pt.life)
        ctx.beginPath()
        ctx.arc(pt.x, pt.y, pt.size * pt.life, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 1
      }

      // 10. DRAW BULLETS
      state.bullets.forEach(b => {
        ctx.fillStyle = b.color
        ctx.fillRect(b.x - 2, b.y - 12, 4, 14)
      })

      // DRAW ENEMY BULLETS
      state.enemyBullets.forEach(eb => {
        ctx.fillStyle = eb.color
        ctx.beginPath()
        ctx.arc(eb.x, eb.y, 4, 0, Math.PI * 2)
        ctx.fill()
      })

      // 11. DRAW ENEMIES & POWERUPS
      state.enemies.forEach(drawEnemy)
      state.powerups.forEach(drawPowerup)

      // 12. DRAW PLAYER
      if (!state.gameOver) {
        drawPlayer(state.player.x, state.player.y, state.player.invulnerableTimer, state.frameCount)
      }

      animationFrameId = requestAnimationFrame(gameLoop)
    }

    animationFrameId = requestAnimationFrame(gameLoop)

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [])

  return (
    <div className='captain-popx' ref={containerRef}>
      {/* 1. Header Bar: Title, Score, High Score, Mute, Exit */}
      <div className='captain-popx__header'>
        <div className='captain-popx__brand'>
          <span className='captain-popx__icon'>🚀</span>
          <span className='captain-popx__title'>Captain PopX</span>
        </div>

        <div className='captain-popx__stats'>
          <div className='captain-popx__stat'>
            <span className='captain-popx__stat-label'>SCORE</span>
            <span className='captain-popx__stat-val' ref={scoreValRef}>{score}</span>
          </div>
          <div className='captain-popx__stat captain-popx__stat--high'>
            <span className='captain-popx__stat-label'>HIGH</span>
            <span className='captain-popx__stat-val' ref={highScoreValRef}>{highScore}</span>
          </div>
        </div>

        <div className='captain-popx__shields'>
          {[...Array(3)].map((_, i) => (
            <span
              key={i}
              ref={el => (heartsRef.current[i] = el)}
              className={`captain-popx__heart ${i < lives ? 'active' : 'empty'}`}
            >
              ♥
            </span>
          ))}
        </div>

        <div className='captain-popx__actions'>
          {onSwitchGame && (
            <button
              type='button'
              className='captain-popx__btn-icon'
              onClick={onSwitchGame}
              title='Switch to PopEx: Terra Explorer'
            >
              🌍 Explorer
            </button>
          )}
          <button
            type='button'
            className='captain-popx__btn-icon'
            onClick={handleToggleMute}
            title={isMuted ? 'Unmute Sound' : 'Mute Sound'}
          >
            {isMuted ? '🔇' : '🔊'}
          </button>
          <button
            type='button'
            className='captain-popx__btn-close'
            onClick={onExitToDashboard}
            title='Exit to Dashboard'
          >
            ✕
          </button>
        </div>
      </div>

      {/* 2. Floating Dashboard Ready Toast / Alert Notice */}
      {isAppReady && !isDismissedReadyNotice && gameState === 'PLAYING' && (
        <div className='captain-popx__ready-banner'>
          <div className='captain-popx__ready-info'>
            <span className='captain-popx__ready-pulse' />
            <span className='captain-popx__ready-text'>Dashboard is ready!</span>
          </div>
          <div className='captain-popx__ready-btns'>
            <button
              type='button'
              className='captain-popx__btn captain-popx__btn--secondary'
              onClick={() => setIsDismissedReadyNotice(true)}
            >
              Continue Playing
            </button>
            <button
              type='button'
              className='captain-popx__btn captain-popx__btn--primary'
              onClick={onExitToDashboard}
            >
              Go to Dashboard 🚀
            </button>
          </div>
        </div>
      )}

      {/* 3. Minimized App Ready Pill (if user clicked continue playing) */}
      {isAppReady && isDismissedReadyNotice && gameState === 'PLAYING' && (
        <button
          type='button'
          className='captain-popx__ready-pill'
          onClick={onExitToDashboard}
        >
          <span className='captain-popx__ready-pulse' />
          Dashboard Ready • Click to Return 🚀
        </button>
      )}

      {/* 4. Canvas Game Board */}
      <div
        className='captain-popx__canvas-wrapper'
        onPointerMove={handlePointerMove}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
      >
        <canvas ref={canvasRef} className='captain-popx__canvas' />
      </div>

      {/* 5. Game Over Modal Overlay */}
      {gameState === 'GAMEOVER' && (
        <div className='captain-popx__gameover-modal'>
          <div className='captain-popx__gameover-card'>
            <h2 className='captain-popx__gameover-title'>MISSION FAILED</h2>
            <p className='captain-popx__gameover-subtitle'>Captain PopX took critical damage</p>

            <div className='captain-popx__gameover-scores'>
              <div className='captain-popx__gameover-score-row'>
                <span>Final Score:</span>
                <strong>{score}</strong>
              </div>
              <div className='captain-popx__gameover-score-row'>
                <span>Personal Best:</span>
                <strong>{highScore}</strong>
              </div>
            </div>

            {isNewHighScore && (
              <div className='captain-popx__new-record-badge'>
                ★ NEW HIGH SCORE! ★
              </div>
            )}

            <div className='captain-popx__gameover-actions'>
              <button
                type='button'
                className='captain-popx__btn captain-popx__btn--primary'
                onClick={handleRestart}
              >
                Play Again 🔄
              </button>
              {isAppReady && (
                <button
                  type='button'
                  className='captain-popx__btn captain-popx__btn--accent'
                  onClick={onExitToDashboard}
                >
                  Go to Dashboard 🚀
                </button>
              )}
              {!isAppReady && (
                <button
                  type='button'
                  className='captain-popx__btn captain-popx__btn--secondary'
                  onClick={onExitToDashboard}
                >
                  Back to Loading Screen
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 6. Footer Controls Helper */}
      <div className='captain-popx__footer'>
        <span>Controls: <strong>Mouse Drag</strong> to move • <strong>Click</strong> to shoot</span>
      </div>
    </div>
  )
}

export default CaptainPopX
